import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './components/HomePage';
import { HelmetProvider } from 'react-helmet-async';
import CustomerAcquisitionPage from './components/CustomerAcquisitionPage';
import CustomerRetentionValuePage from './components/CustomerRetentionValuePage';
import EngagementInteractionPage from './components/EngagementInteractionPage';
import EmailMarketingPerformancePage from './components/EmailMarketingPerformancePage';
import WebContentAnalysisPage from './components/WebContentAnalysisPage';
import AdvertisingMediaPerformancePage from './components/AdvertisingMediaPerformancePage';
import SocialMediaInfluencersPage from './components/SocialMediaInfluencersPage';
import GrowthMarketSharePage from './components/GrowthMarketSharePage';
import BlogPage from './components/BlogPage';
import GlossaryPage from './components/GlossaryPage';
import MasteringEmailMarketing from './components/blog/MasteringEmailMarketing';
import SocialMediaROICalculator from './components/SocialMediaROICalculator';
import GrowthRateCalculator from './components/GrowthRateCalculator';
import MarketShareCalculator from './components/MarketShareCalculator';
import MarketPenetrationRateCalculator from './components/MarketPenetrationRateCalculator';
import MarketConcentrationCalculator from './components/MarketConcentrationCalculator';
import PriceElasticityOfDemandCalculator from './components/PriceElasticityOfDemandCalculator';
import InfluencerMarketingROICalculator from './components/InfluencerMarketingROICalculator';
import OrganicSocialMediaReachCalculator from './components/OrganicSocialMediaReachCalculator';
import SocialMediaEngagementRateCalculator from './components/SocialMediaEngagementRateCalculator';
import SocialMediaInteractionRateCalculator from './components/SocialMediaInteractionRateCalculator';
import FormCompletionRateCalculator from './components/FormCompletionRateCalculator';
import CustomerSurveyResponseRateCalculator from './components/CustomerSurveyResponseRateCalculator';
import CustomerSatisfactionScoreCalculator from './components/CustomerSatisfactionScoreCalculator';
import CustomerReferralRateCalculator from './components/CustomerReferralRateCalculator';
import NetPromoterScoreCalculator from './components/NetPromoterScoreCalculator';
import GlossaryLetterPage from './components/GlossaryLetterPage';
import GlossaryTermPage from './components/GlossaryTermPage';
import AboutPage from './components/AboutPage';

// Import all calculator components
import ROICalculator from './components/ROICalculator';
import ConversionRateCalculator from './components/ConversionRateCalculator';
import CustomerLifetimeValueCalculator from './components/CustomerLifetimeValueCalculator';
import ABTestSignificanceCalculator from './components/ABTestSignificanceCalculator';
import CPCCalculator from './components/CPCCalculator';
import CPACalculator from './components/CPACalculator';
import CPMCalculator from './components/CPMCalculator';
import CostPerLeadCalculator from './components/CostPerLeadCalculator';
import CACCalculator from './components/CACCalculator';
import CostPerAppInstallCalculator from './components/CostPerAppInstallCalculator';
import CustomerRetentionRateCalculator from './components/CustomerRetentionRateCalculator';
import AverageCustomerLifespanCalculator from './components/AverageCustomerLifespanCalculator';
import AverageOrderValueCalculator from './components/AverageOrderValueCalculator';
import AverageCartValueCalculator from './components/AverageCartValueCalculator';
import CartAbandonmentRateCalculator from './components/CartAbandonmentRateCalculator';
import LoyaltyProgramROICalculator from './components/LoyaltyProgramROICalculator';

// Import Email Marketing Performance calculators
import EmailOpenRateCalculator from './components/EmailOpenRateCalculator';
import EmailClickThroughRateCalculator from './components/EmailClickThroughRateCalculator';
import EmailUnsubscribeRateCalculator from './components/EmailUnsubscribeRateCalculator';
import EmailCampaignROICalculator from './components/EmailCampaignROICalculator';
import NewsletterChurnRateCalculator from './components/NewsletterChurnRateCalculator';

// Import Web Content Analysis calculators
import BounceRateCalculator from './components/BounceRateCalculator';
import BlogPostReadRateCalculator from './components/BlogPostReadRateCalculator';
import CostPerWebsiteVisitCalculator from './components/CostPerWebsiteVisitCalculator';
import LandingPageConversionRateCalculator from './components/LandingPageConversionRateCalculator';
import TimeOnSiteValueCalculator from './components/TimeOnSiteValueCalculator';
import ContentMarketingROICalculator from './components/ContentMarketingROICalculator';

// Import Advertising and Media Performance calculators
import AdvertisingROICalculator from './components/AdvertisingROICalculator';
import ROASCalculator from './components/ROASCalculator';
import ShareOfVoiceCalculator from './components/ShareOfVoiceCalculator';
import YouTubeViewTimeValueCalculator from './components/YouTubeViewTimeValueCalculator';
import CostPerVideoViewCalculator from './components/CostPerVideoViewCalculator';
import BannerAdClickThroughRateCalculator from './components/BannerAdClickThroughRateCalculator';
import EffectiveCostPerMilleCalculator from './components/EffectiveCostPerMilleCalculator';

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initApp = async () => {
      try {
        setIsLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Une erreur est survenue');
        setIsLoading(false);
      }
    };

    initApp();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center text-red-600 p-4">
          <p>Erreur: {error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <HelmetProvider>
      <Router>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
          <Header />
          <main className="flex-grow">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/customer-acquisition" element={<CustomerAcquisitionPage />} />
              <Route path="/customer-retention-value" element={<CustomerRetentionValuePage />} />
              <Route path="/engagement-interaction" element={<EngagementInteractionPage />} />
              <Route path="/email-marketing-performance" element={<EmailMarketingPerformancePage />} />
              <Route path="/web-content-analysis" element={<WebContentAnalysisPage />} />
              <Route path="/advertising-media-performance" element={<AdvertisingMediaPerformancePage />} />
              <Route path="/social-media-influencers" element={<SocialMediaInfluencersPage />} />
              <Route path="/growth-market-share" element={<GrowthMarketSharePage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/glossary" element={<GlossaryPage />} />
              <Route path="/blog/mastering-email-marketing" element={<MasteringEmailMarketing />} />
              <Route path="/glossary/:letter" element={<GlossaryLetterPage />} />
              <Route path="/glossary-term/:term" element={<GlossaryTermPage />} />
              <Route path="/about" element={<AboutPage />} />

              {/* Calculator routes */}
              <Route path="/calculators/return-on-investment" element={<ROICalculator />} />
              <Route path="/calculators/conversion-rate" element={<ConversionRateCalculator />} />
              <Route path="/calculators/customer-lifetime-value" element={<CustomerLifetimeValueCalculator />} />
              <Route path="/calculators/ab-test-significance" element={<ABTestSignificanceCalculator />} />
              <Route path="/calculators/cost-per-click" element={<CPCCalculator />} />
              <Route path="/calculators/cost-per-acquisition" element={<CPACalculator />} />
              <Route path="/calculators/cost-per-mille" element={<CPMCalculator />} />
              <Route path="/calculators/cost-per-lead" element={<CostPerLeadCalculator />} />
              <Route path="/calculators/customer-acquisition-cost" element={<CACCalculator />} />
              <Route path="/calculators/cost-per-app-install" element={<CostPerAppInstallCalculator />} />
              <Route path="/calculators/customer-retention-rate" element={<CustomerRetentionRateCalculator />} />
              <Route path="/calculators/average-customer-lifespan" element={<AverageCustomerLifespanCalculator />} />
              <Route path="/calculators/average-order-value" element={<AverageOrderValueCalculator />} />
              <Route path="/calculators/average-cart-value" element={<AverageCartValueCalculator />} />
              <Route path="/calculators/cart-abandonment-rate" element={<CartAbandonmentRateCalculator />} />
              <Route path="/calculators/loyalty-program-roi" element={<LoyaltyProgramROICalculator />} />

              {/* Email Marketing Performance calculator routes */}
              <Route path="/calculators/email-open-rate" element={<EmailOpenRateCalculator />} />
              <Route path="/calculators/email-click-through-rate" element={<EmailClickThroughRateCalculator />} />
              <Route path="/calculators/email-unsubscribe-rate" element={<EmailUnsubscribeRateCalculator />} />
              <Route path="/calculators/email-campaign-roi" element={<EmailCampaignROICalculator />} />
              <Route path="/calculators/newsletter-churn-rate" element={<NewsletterChurnRateCalculator />} />

              {/* Web Content Analysis calculator routes */}
              <Route path="/calculators/bounce-rate" element={<BounceRateCalculator />} />
              <Route path="/calculators/blog-post-read-rate" element={<BlogPostReadRateCalculator />} />
              <Route path="/calculators/cost-per-website-visit" element={<CostPerWebsiteVisitCalculator />} />
              <Route path="/calculators/landing-page-conversion-rate" element={<LandingPageConversionRateCalculator />} />
              <Route path="/calculators/time-on-site-value" element={<TimeOnSiteValueCalculator />} />
              <Route path="/calculators/content-marketing-roi" element={<ContentMarketingROICalculator />} />

              {/* Advertising and Media Performance calculator routes */}
              <Route path="/calculators/advertising-roi" element={<AdvertisingROICalculator />} />
              <Route path="/calculators/return-on-ad-spend" element={<ROASCalculator />} />
              <Route path="/calculators/share-of-voice" element={<ShareOfVoiceCalculator />} />
              <Route path="/calculators/youtube-view-time-value" element={<YouTubeViewTimeValueCalculator />} />
              <Route path="/calculators/cost-per-video-view" element={<CostPerVideoViewCalculator />} />
              <Route path="/calculators/banner-ad-click-through-rate" element={<BannerAdClickThroughRateCalculator />} />
              <Route path="/calculators/effective-cost-per-mille" element={<EffectiveCostPerMilleCalculator />} />
              <Route path="/calculators/social-media-engagement-rate" element={<SocialMediaEngagementRateCalculator />} />
              <Route path="/calculators/social-media-interaction-rate" element={<SocialMediaInteractionRateCalculator />} />
              <Route path="/calculators/form-completion-rate" element={<FormCompletionRateCalculator />} />
              <Route path="/calculators/customer-survey-response-rate" element={<CustomerSurveyResponseRateCalculator />} />
              <Route path="/calculators/customer-satisfaction-score" element={<CustomerSatisfactionScoreCalculator />} />
              <Route path="/calculators/customer-referral-rate" element={<CustomerReferralRateCalculator />} />
              <Route path="/calculators/net-promoter-score" element={<NetPromoterScoreCalculator />} />
              <Route path="/calculators/social-media-roi" element={<SocialMediaROICalculator />} />
              <Route path="/calculators/influencer-marketing-roi" element={<InfluencerMarketingROICalculator />} />
              <Route path="/calculators/organic-social-media-reach" element={<OrganicSocialMediaReachCalculator />} />
              <Route path="/calculators/growth-rate" element={<GrowthRateCalculator />} />
              <Route path="/calculators/market-share" element={<MarketShareCalculator />} />
              <Route path="/calculators/market-penetration-rate" element={<MarketPenetrationRateCalculator />} />
              <Route path="/calculators/market-concentration" element={<MarketConcentrationCalculator />} />
              <Route path="/calculators/price-elasticity-of-demand" element={<PriceElasticityOfDemandCalculator />} />
            </Routes>
          </main>
          <Footer />
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;
